import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { faqs } from '../data/mock';
import { submitContactForm } from '../services/apiService';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'devis',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Pré-remplir le sujet depuis l'URL (ex: /contact?subject=location&message=Machine%20X)
  useEffect(() => {
    const subjectParam = searchParams.get('subject');
    const messageParam = searchParams.get('message');
    if (subjectParam || messageParam) {
      setFormData((prev) => ({
        ...prev,
        ...(subjectParam ? { subject: subjectParam } : {}),
        ...(messageParam ? { message: messageParam } : {}),
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await submitContactForm(formData);
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: 'devis',
          message: ''
        });
      }, 3000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Une question ? Un projet laser en tête ?<br />
            Parlons-en ensemble. Conseil gratuit et sans engagement.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                  Nos coordonnées
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans votre projet.
                </p>
                <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border-l-4" style={{ borderColor: '#5C3A21' }}>
                  <p className="text-sm font-semibold" style={{ color: '#5C3A21' }}>
                    💻 Visioconférence disponible
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    Découvrez nos machines en direct depuis chez vous ! Nous proposons des démonstrations en visio pour vous présenter le fonctionnement des machines.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#5C3A21' }}>
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#5C3A21' }}>Email</h3>
                    <a href="mailto:contact@akmlaser.com" className="text-gray-700 hover:text-gray-900 transition-colors">
                      contact@akmlaser.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#5C3A21' }}>
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#5C3A21' }}>Téléphone</h3>
                    <a href="tel:+33761203370" className="text-gray-700 hover:text-gray-900 transition-colors">
                      07 61 20 33 70
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Lun-Ven : 9h-18h</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#5C3A21' }}>
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#5C3A21' }}>Siège social</h3>
                    <p className="text-gray-700">
                      465 route de l'oasis<br />
                      Moidieu<br />
                      <span className="font-semibold text-amber-800">Sur rendez-vous uniquement</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 rounded-xl border-2 border-amber-200" style={{ backgroundColor: '#F5EBDD' }}>
                <h3 className="font-bold mb-3" style={{ color: '#5C3A21' }}>Horaires d'ouverture</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Lundi - Vendredi</span>
                    <span className="font-semibold">9h - 18h</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Samedi</span>
                    <span className="font-semibold">Sur RDV</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Dimanche</span>
                    <span className="font-semibold">Fermé</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 md:p-12 rounded-2xl shadow-xl border border-amber-100">
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
                  Envoyez-nous un message
                </h2>

                {submitted ? (
                  <div className="py-12 text-center">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-green-600">Message envoyé !</h3>
                    <p className="text-gray-700">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700">{error}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold mb-2 text-gray-900">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none"
                          placeholder="Jean Dupont"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold mb-2 text-gray-900">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none"
                          placeholder="jean@exemple.fr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold mb-2 text-gray-900">
                          Téléphone *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none"
                          placeholder="+33 6 12 34 56 78"
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block text-sm font-semibold mb-2 text-gray-900">
                          Entreprise
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none"
                          placeholder="Nom de votre entreprise"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold mb-2 text-gray-900">
                        Objet de votre demande *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none"
                      >
                        <option value="devis">Demande de devis</option>
                        <option value="location">Demande de location</option>
                        <option value="demo">Réserver une démonstration</option>
                        <option value="conseil">Demande de conseil</option>
                        <option value="sav">SAV / Support technique</option>
                        <option value="autre">Autre question</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold mb-2 text-gray-900">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 outline-none resize-none"
                        placeholder="Décrivez votre projet ou votre question..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      style={{ backgroundColor: '#5C3A21' }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer le message
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>

                    <p className="text-sm text-gray-600 text-center">
                      * Champs obligatoires
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24" style={{ backgroundColor: '#F5EBDD' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Questions fréquentes
            </h2>
            <p className="text-xl text-gray-700">
              Les réponses aux questions les plus posées
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-white rounded-xl border border-amber-100 shadow-md overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline group">
                  <span className="font-semibold text-lg group-hover:text-amber-700 transition-colors" style={{ color: '#5C3A21' }}>
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-700 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;
